import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Package, User, DollarSign, Gift } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicPortfolioPeek() {
  const { portfolioId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPortfolioData();
  }, [portfolioId]);

  const fetchPortfolioData = async () => {
    try {
      const response = await axios.get(`${API}/public/portfolio/${portfolioId}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFFFF' }}>
        <div className="text-xl" style={{ color: '#0B0B0B' }}>Loading...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFFFF' }}>
        <div className="text-xl" style={{ color: '#FF6B6B' }}>Portfolio not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#FFFFFF', padding: '40px 20px' }}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{data.portfolio.name} | Portfolio Snapshot</title>
        <meta name="description" content={data.portfolio.description} />
        <meta property="og:title" content={`${data.portfolio.name} - NFT Portfolio`} />
        <meta property="og:description" content={data.portfolio.description} />
        {data.portfolio.image_url && <meta property="og:image" content={data.portfolio.image_url} />}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={data.portfolio.name} />
        <meta name="twitter:description" content={data.portfolio.description} />
        {data.portfolio.image_url && <meta name="twitter:image" content={data.portfolio.image_url} />}
      </Helmet>

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#47FFB0', fontFamily: 'Space Grotesk' }}>
            {data.portfolio.name}
          </h1>
          <p className="text-lg" style={{ color: '#0B0B0B' }}>
            Portfolio Snapshot
          </p>
        </div>

        {/* NFT Image */}
        {data.portfolio.image_url && (
          <div className="mb-8">
            <img 
              src={data.portfolio.image_url} 
              alt={data.portfolio.name}
              className="w-full max-w-2xl mx-auto rounded-lg shadow-2xl"
              style={{ maxHeight: '500px', objectFit: 'cover' }}
            />
          </div>
        )}

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Creator Info */}
          <Card className="glass-card border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ color: '#4B9DFF' }}>
                <User className="w-5 h-5" />
                Creator
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-semibold" style={{ color: '#0B0B0B' }}>{data.creator.name}</p>
              <p className="text-sm" style={{ color: '#999' }}>{data.creator.email}</p>
            </CardContent>
          </Card>

          {/* Portfolio Details */}
          <Card className="glass-card border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ color: '#4B9DFF' }}>
                <Package className="w-5 h-5" />
                Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span style={{ color: '#999' }}>Category:</span>
                  <span style={{ color: '#0B0B0B' }}>{data.portfolio.category}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: '#999' }}>Units:</span>
                  <span style={{ color: '#0B0B0B' }}>{data.portfolio.total_units}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: '#999' }}>Status:</span>
                  <span style={{ color: '#47FFB0' }}>{data.portfolio.status}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Description */}
        <Card className="glass-card border-primary/20 mb-8">
          <CardHeader>
            <CardTitle style={{ color: '#4B9DFF' }}>About</CardTitle>
          </CardHeader>
          <CardContent>
            <p style={{ color: '#0B0B0B' }}>{data.portfolio.description}</p>
          </CardContent>
        </Card>

        {/* Utilities Preview */}
        {data.utilities && data.utilities.length > 0 && (
          <Card className="glass-card border-primary/20 mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ color: '#4B9DFF' }}>
                <Gift className="w-5 h-5" />
                Utilities & Perks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data.utilities.slice(0, 5).map((utility, index) => (
                  <div key={index} className="p-3 rounded" style={{ background: 'rgba(71, 255, 176, 0.05)' }}>
                    <p className="font-semibold" style={{ color: '#47FFB0' }}>{utility.title || `Utility ${index + 1}`}</p>
                    <p className="text-sm" style={{ color: '#0B0B0B' }}>{utility.description || 'Premium utility'}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payout Rules */}
        <Card className="glass-card border-primary/20 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#4B9DFF' }}>
              <DollarSign className="w-5 h-5" />
              Payout Rules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span style={{ color: '#999' }}>Kickback:</span>
                <span style={{ color: '#47FFB0' }}>{data.portfolio.kickback_percent}%</span>
              </div>
              {data.payout_rules.cadence && (
                <div className="flex justify-between">
                  <span style={{ color: '#999' }}>Payout Cadence:</span>
                  <span style={{ color: '#0B0B0B' }}>{data.payout_rules.cadence}</span>
                </div>
              )}
              {data.payout_rules.royalty_split && (
                <div className="flex justify-between">
                  <span style={{ color: '#999' }}>Royalty Split (Creator):</span>
                  <span style={{ color: '#0B0B0B' }}>{data.payout_rules.royalty_split}%</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contract Address */}
        {data.portfolio.contract_address && (
          <Card className="glass-card border-primary/20">
            <CardHeader>
              <CardTitle style={{ color: '#4B9DFF' }}>Contract Address</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-mono text-sm break-all" style={{ color: '#47FFB0' }}>
                {data.portfolio.contract_address}
              </p>
              <a 
                href={`https://amoy.polygonscan.com/address/${data.portfolio.contract_address}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm mt-2 inline-block"
                style={{ color: '#4B9DFF' }}
              >
                View on Polygonscan →
              </a>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
